<template>
    <article class="w-full"
             :class="{ '-is-appended-to-prev-block': appendToPreviousBlock, 'o-container--bleed': parentAlias }">
        <div :class="{ 'py-35 px-40 has-background': backgroundColor, 'px-10 sm:px-0': !backgroundColor }"
             :style="style">
            <div class="w-full break-words">
                <SmartHeadline v-if="content.headline"
                               :level="isFirstBlock ? 'h1' : 'h2'"
                               :style-as-h1="isFirstBlock"
                               class="mb-10">
                    {{ content.headline }}
                </SmartHeadline>
                <section ref="htmlContentRef"
                         v-delegate-html-links
                         v-delegate-on-img-error
                         class="wysiwyg text-block"
                         v-html="bodyText(content.bodyText)"/>
                <CallToAction v-if="content.callToAction"
                              class="cta--rounded mt-20"
                              :hex-color="content.callToActionColor"
                              :text="content.callToAction.name"
                              :href="content.callToAction.url"
                              :target="content.callToAction.target"/>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import { ref, Ref, ComputedRef, computed, onMounted, onBeforeUnmount, PropType, StyleValue, useTemplateRef } from 'vue';
import { FullWidthTextBlockViewModel } from '@/types/contentServerContract';
import SmartHeadline from '@/core/ui-components/SmartHeadline.vue';
import tableHtmlService from './tableHtml.service';
import { hexToRgbValues, luma } from '@/core/color/color.service';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwind from '@/styling/tailwind.config';
const tailwindConfiguration = resolveConfig(tailwind);

const props = defineProps({
    content: {
        type: Object as PropType<FullWidthTextBlockViewModel>,
        required: true
    },
    isFirstBlock: {
        type: Boolean,
        required: true
    },
    parentAlias: {
        type: String,
        required: false,
        default: null
    }
});

const backgroundColor = computed(() => {
    return props.content.customBackgroundColor
        ? props.content.customBackgroundColor
        : props.content.backgroundColor ? '#' + props.content.backgroundColor : undefined;
});

const textColor = computed(() => {
    if (!backgroundColor.value) return undefined;
    const { red, green, blue } = hexToRgbValues(backgroundColor.value);
    const isDarkBackground = luma(red, green, blue) <= 0.5;
    return isDarkBackground ? tailwindConfiguration.theme.colors.white : tailwindConfiguration.theme.colors.black;
});

const style: ComputedRef<StyleValue> = computed(() => {
    return {
        backgroundColor: backgroundColor.value,
        color: textColor.value
    };
});

const appendToPreviousBlock = computed(() => {
    return props.content.appendToPreviousBlock && !props.isFirstBlock;
});

function bodyText(html: string): string {
    return tableHtmlService.wrapTableInOuterDivs(html);
}

const htmlContentRef = useTemplateRef<HTMLElement | null>('htmlContentRef');

onMounted(() => {
    if (htmlContentRef.value) {
        tableHtmlService.registerTableOverflow(htmlContentRef.value);
    }
});

onBeforeUnmount(() => {
    if (htmlContentRef.value) {
        tableHtmlService.unregisterTableOverflow(htmlContentRef.value);
    }
});
</script>
